import React, { useState } from 'react';
import { Popup, Icon, Loader, Header, Label, Button } from 'semantic-ui-react';
import {
  ContractListDto,
  ContractStatusHistoryDto,
  contractService,
} from '../../services/contracts/ContractService';
import { useAuth } from '../../context/AuthContext';
import { getContractStatusText } from '../../utils/contractStatusUtils';
import StatusHistoryModal from './StatusHistoryModal';
import GroupStatusHistoryModal from './GroupStatusHistoryModal';

interface StatusHistoryPopoverProps {
  contractId: string;
  currentStatus: number | null;
  currentStatusText: string;
  updatedAt: string;
  trigger?: React.ReactNode;
  historyTitleSuffix?: string;
  groupContracts?: ContractListDto[];
}

const StatusHistoryPopover: React.FC<StatusHistoryPopoverProps> = ({
  contractId,
  currentStatus,
  currentStatusText,
  updatedAt,
  trigger,
  historyTitleSuffix,
  groupContracts,
}) => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('Admin');
  
  const [history, setHistory] = useState<ContractStatusHistoryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFullModal, setShowFullModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isGroupHistory = Boolean(groupContracts?.length);
  const shouldShowFullHistoryButton = isGroupHistory || history.length > 3;

  const loadHistory = async () => {
    if (history.length > 0) return; // Já carregou
    
    setLoading(true);
    setError(null);
    try {
      const data = await contractService.getContractStatusHistory(contractId);
      setHistory(data);
    } catch (err: any) {
      console.error('Erro ao carregar histórico:', err);
      setError(err.message || 'Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month} ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  // Se não há status, não mostrar o componente
  if (currentStatus === null || currentStatus === undefined) {
    return null;
  }

  return (
    <>
      <Popup
        trigger={trigger || (
          <Icon 
            name="history" 
            style={{ cursor: 'pointer', marginLeft: '8px' }}
            color="blue"
            size="small"
          />
        )}
        flowing
        hoverable
        on="hover"
        onOpen={loadHistory}
        position="bottom center"
        wide="very"
      >
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <Loader active inline size="small" />
            <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666' }}>
              Carregando histórico...
            </div>
          </div>
        ) : error ? (
          <div style={{ padding: '10px', maxWidth: '300px' }}>
            <Icon name="warning circle" color="red" />
            <span style={{ color: '#666' }}>{error}</span>
          </div>
        ) : history.length === 0 ? (
          <div style={{ padding: '10px', maxWidth: '300px' }}>
            <Icon name="info circle" color="grey" />
            <span style={{ color: '#666' }}>Nenhum histórico disponível</span>
            {isGroupHistory && (
              <Button
                size="mini"
                fluid
                basic
                color="blue"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullModal(true);
                }}
                style={{ marginTop: '12px' }}
              >
                <Icon name="list" />
                Ver histórico completo do grupo
              </Button>
            )}
          </div>
        ) : (
          <div style={{ maxWidth: '400px', padding: '10px' }}>
            <Header size="tiny" style={{ marginBottom: '12px' }}>
              <Icon name="history" />
              Histórico de Status{historyTitleSuffix}
            </Header>
            
            {/* Mostrar últimos 3 status */}
            {history.slice(0, 3).map((item, index) => (
              <div 
                key={item.id} 
                style={{ 
                  marginBottom: index < 2 ? '14px' : '0',
                  paddingBottom: index < 2 ? '12px' : '0',
                  borderBottom: index < 2 ? '1px solid #eee' : 'none'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <Icon 
                    name={index === 0 ? 'check circle' : 'arrow right'} 
                    color={index === 0 ? 'green' : 'grey'} 
                  />
                  <strong style={{ fontSize: '0.95em' }}>{getContractStatusText(item.status)}</strong>
                  {index === 0 && <Label size="mini" color="green">Atual</Label>}
                </div>
                
                <div style={{ fontSize: '0.85em', color: '#666', marginLeft: '24px' }}>
                  <div style={{ marginBottom: '2px' }}>
                    <Icon name="calendar alternate outline" size="small" />
                    {formatDate(item.changedAt)}
                    {isAdmin && item.changedByUserName && (
                      <span> - {item.changedByUserName}</span>
                    )}
                    {!isAdmin && (
                      <span> - Sistema/Usuário</span>
                    )}
                  </div>
                  
                  {item.changeReason && (
                    <div style={{ 
                      fontSize: '0.9em', 
                      color: '#888', 
                      fontStyle: 'italic',
                      marginTop: '4px'
                    }}>
                      <Icon name="info circle" size="small" />
                      {item.changeReason}
                    </div>
                  )}

                  {item.groupId && (
                    <div style={{ fontSize: '0.8em', color: '#999', marginTop: '4px' }}>
                      <Icon name="sitemap" size="small" />
                      <span style={{ marginLeft: '4px' }}>GroupId: {item.groupId}</span>
                    </div>
                  )}
                  
                  {item.previousStatus != null && index > 0 && (
                    <div style={{ fontSize: '0.85em', color: '#999', marginTop: '3px' }}>
                      De: {getContractStatusText(item.previousStatus ?? undefined)}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {shouldShowFullHistoryButton && (
              <Button 
                size="mini" 
                fluid 
                basic
                color="blue"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullModal(true);
                }}
                style={{ marginTop: '12px' }}
              >
                <Icon name="list" />
                {isGroupHistory
                  ? 'Ver histórico completo do grupo'
                  : `Ver histórico completo (${history.length} mudanças)`}
              </Button>
            )}
          </div>
        )}
      </Popup>

      {/* Modal com histórico completo */}
      {showFullModal && isGroupHistory && groupContracts ? (
        <GroupStatusHistoryModal
          isOpen={showFullModal}
          onClose={() => setShowFullModal(false)}
          groupContracts={groupContracts}
        />
      ) : showFullModal ? (
        <StatusHistoryModal
          isOpen={showFullModal}
          onClose={() => setShowFullModal(false)}
          history={history}
          contractId={contractId}
        />
      ) : null}
    </>
  );
};

export default StatusHistoryPopover;
